import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { TableColumnType } from "src/app/constants/table";
import { AdminEventOption, MyQLEAdminEventModel, MyQLEChatRequestModel, MyQLEChatResponseModel, MyQLEIDPayload, MyQLERequestCriteria, MyQLERequestModel } from "src/app/models/qualifying-life-event";
import { TableColumn } from "src/app/models/table";
import { QLEService } from "src/app/services/qle.service";
import { Router } from "@angular/router";
import { ToastService } from "src/app/services/toast.service";
import { ToastType } from "src/app/constants/toast-type";
import { FileUploadService } from "src/app/services/file-upload.service";
import { DocumentPayload, QLEDocument } from "src/app/models/document";
import { HelperService } from "src/app/services/helper.service";
import { FileUploadDialogModel } from "src/app/models/dialog";
import { DialogFileUploadComponent } from "src/app/components/shared/dialog/file-upload.component";
import { SessionService } from "src/app/services/storage/session.service";
import { FormValidationService } from "src/app/services/form-validation.service";
import { MemberService } from "src/app/services/member.service";


@Component({
    selector: 'mwp-member-qle-form',
    templateUrl: "./qle-form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class QLEFormComponent implements OnInit {
    @Input() public myQLERequestModel: MyQLERequestModel;
    @Input() public isNewRequest = false;

    public displayNewRequestForm: boolean;
    public displayRequestForm = false;

    public adminEventOptions: AdminEventOption[];

    public newRequestForm: FormGroup;
    public requestForm: FormGroup;
    public newNoteForm: FormGroup;
    public QleID: number;
    public chats: MyQLEChatResponseModel;
    public documents: QLEDocument[];
    public cancelText: string;
    public adminEventOptionID: number;
    public canEdit: boolean;
    public canCancel: boolean;
    public canDeleteDocs: boolean;
    public canDoAction: boolean;

    public files: File[];
    public areFilesUpdated: boolean;
    public noteInformationText: string;
    public currentMyQLERequestModel: MyQLERequestModel;
    public memberName: string;



    @Output() public closeEmit: EventEmitter<any> = new EventEmitter<any>();
    @Output() public reloadEmit: EventEmitter<any> = new EventEmitter<any>();


    public documentTableData: MatTableDataSource<QLEDocument>;
    public documentColumns: TableColumn[] = [
        {
            columnName: 'Document Name',
            propertyId: 'documentName',
            columnType: TableColumnType.label,
        },
        {
            columnName: 'Delete',
            propertyId: 'id',
            dataTypeText: 'Delete',
            columnType: TableColumnType.button,
            width: '30%',
            cssCustomClass: 'mwp-button-delete'
        }
    ];
    constructor(
        private qleService: QLEService,
        private formBuilder: FormBuilder,
        private dialog: MatDialog,
        private router: Router,
        private toastService: ToastService,
        private fileUploadService: FileUploadService,
        private helperService: HelperService,
        private sessionService: SessionService,
        private memberService: MemberService
    ) {
    }

    ngOnInit(): void {
        this.memberName = this.sessionService.session.member.memberName;
        if (this.isNewRequest) {
            this.qleService.getLifeEventOptions().subscribe((response: MyQLEAdminEventModel) => {
                this.adminEventOptions = response.adminEventOptions;
            });

            this.newRequestForm = this.formBuilder.group({
                adminEventId: [null, Validators.required],
                eventDate: [null, [Validators.required, Validators.compose([FormValidationService.validMaterialDate(true, true)])]],
            });

            this.displayRequestForm = false;
            this.displayNewRequestForm = true;
        } else {
            this.loadRequestForm(this.myQLERequestModel);
        }
    }

    public loadRequestForm(myQLERequestModel: MyQLERequestModel): void {
        this.canEdit = myQLERequestModel.canEdit;

        this.requestForm = this.formBuilder.group({
            lifeEventName: [{ value: myQLERequestModel.adminEventOptionDescription, disabled: true }],
            eventDate: [{ value: this.helperService.convertDateToMaterialDate(myQLERequestModel.eventDate), disabled: !myQLERequestModel.canEdit }, myQLERequestModel.canEdit ? Validators.required : null],
        });

        this.newNoteForm = this.formBuilder.group({
            note: [null, Validators.required],
        });

        const l_payload: MyQLEIDPayload = new MyQLEIDPayload();
        l_payload.id = myQLERequestModel.id;
        this.QleID = myQLERequestModel.id;
        this.noteInformationText = myQLERequestModel.noteInformationText;
        this.qleService.getQLERequestByID(l_payload).subscribe((response: MyQLERequestModel) => {
            this.documents = response.myQLERequestDocuments;
            this.loadDocumentTable();
        });

        this.loadChats();
        this.adminEventOptionID = myQLERequestModel.enrollAdminEventOptionID;
        this.cancelText = myQLERequestModel.isDraft ? "Cancel Draft" : "Cancel Life Event Request";

        this.canCancel = myQLERequestModel.canCancel;
        this.canDeleteDocs = myQLERequestModel.canDeleteDocs;
        this.canDoAction = myQLERequestModel.canDoAction;
        this.currentMyQLERequestModel = myQLERequestModel;

        this.displayNewRequestForm = false;
        this.displayRequestForm = true;
    }

    public loadDocumentTable(): void {
        this.documentTableData = new MatTableDataSource<QLEDocument>();
        this.documentTableData.data = this.documents;
    }

    public loadChats(): void {
        const l_payload: MyQLEIDPayload = new MyQLEIDPayload();
        l_payload.id = this.QleID;
        this.qleService.getChatHistory(l_payload).subscribe((response: MyQLEChatResponseModel) => {
            this.chats = response;
        });
    }

    public cancelForm(): void {
        this.closeEmit.emit();
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/false-endpoint', { skipLocationChange: true })
            .then(() => {
                this.router.navigateByUrl(currentUrl);
            });

    }

    public saveQLE(): void {
        if (this.displayNewRequestForm) {
            const l_payload: MyQLERequestCriteria = new MyQLERequestCriteria();
            if (this.isNewRequest) {
                l_payload.enrollAdminEventOptionID = this.newRequestForm.get('adminEventId').value;
            }
            if (this.displayRequestForm) {
                l_payload.enrollAdminEventOptionID = this.adminEventOptionID;
                l_payload.id = this.QleID;
            }
            l_payload.eventDate = this.newRequestForm.get('eventDate').value;
            this.qleService.saveQLE(l_payload).subscribe((response: MyQLERequestModel) => {
                if (this.isNewRequest)
                    this.memberService.resetMessageCenter();
                this.loadRequestForm(response);
            });
        } else {
            if (this.areFilesUpdated) {
                this.saveDocuments();
            }
        }
    }

    public submitQLE(): void {
        this.saveQLE();
        const l_payload: MyQLEIDPayload = new MyQLEIDPayload();
        l_payload.id = this.QleID;
        this.qleService.submitQLE(l_payload).subscribe(() => {
            this.reloadEmit.emit();
        });
    }

    public cancelQLE(): void {
        const l_payload: MyQLEIDPayload = new MyQLEIDPayload();
        l_payload.id = this.QleID;
        this.qleService.cancelQLE(l_payload).subscribe(() => {
            this.reloadEmit.emit();
        });
    }

    public addNote(): void {
        const l_payload: MyQLEChatRequestModel = new MyQLEChatRequestModel;
        l_payload.chat = this.newNoteForm.get('note').value;
        l_payload.qLERequestID = this.QleID;

        this.qleService.saveChat(l_payload).subscribe(() => {
            this.newNoteForm.get('note').reset();
            this.loadChats();
        });
    }

    public openUploadDialog(): void {
        const l_FileUploadDialogModel = new FileUploadDialogModel();
        l_FileUploadDialogModel.qLERequestID = this.QleID;
        const dialogRef = this.dialog.open(DialogFileUploadComponent, { data: l_FileUploadDialogModel, panelClass: 'mwp-modal-override-600' });

        dialogRef.afterClosed().subscribe((response: any) => {
            if (response?.dialogResult) {
                this.documents = response.dialogResult as QLEDocument[];
                this.loadDocumentTable();
            }
        });
    }

    // TODO: manually reset cache for document upload (clear table on upload)
    public uploadDocuments(event: File[]): void {
        this.areFilesUpdated = true;
        this.files = event;
        this.toastService.buildToastMessage(ToastType.success, "Document Selected", "Please click Save Changes to upload document to this Life Event Request");
    }

    public saveDocuments(): void {
        const l_payload: DocumentPayload = new DocumentPayload();
        l_payload.qleRequestID = this.QleID;
        l_payload.files = this.files;
        this.fileUploadService.uploadQLEDocuments(l_payload).subscribe((response: QLEDocument[]) => {
            this.documents = response;
            this.loadDocumentTable();
        });
        this.areFilesUpdated = false;
        this.files = null;
    }

    public deleteDocument(event: QLEDocument): void {
        const l_payload: MyQLEIDPayload = new MyQLEIDPayload();
        l_payload.id = event.id;
        this.qleService.deleteDocuments(l_payload).subscribe((response: QLEDocument[]) => {
            this.documents = response;
            this.loadDocumentTable();
        });
    }

    public submit(): void {
        let l_CanSubmit: boolean = true;

        if (this.chats.chatEntries.length < 1) {
            this.toastService.buildToastMessage(ToastType.warning, "Submit Error", "Please add at least one note describing your document");
            l_CanSubmit = false;
        }
        if (this.documents.length < 1) {
            this.toastService.buildToastMessage(ToastType.warning, "Submit Error", "Please upload and save at least one document");
            l_CanSubmit = false;
        }

        if (l_CanSubmit) {
            this.submitQLE();
        }
    }
}
